/**
 * WB4.4 GAP-12 — Auxiliary Timeline/Audit API scope for unified runtime routes.
 * PTG reference slice plus promoted non-PTG runtime route families.
 */

import { isPtgReferenceWorkbenchScope } from './ptg-reference-routes';
import { isDomainCockpitPilotDomain } from './workbench-domain-cockpit-invariants';

/** Runtime route families on BusinessWorkbenchShell outside the PTG reference slice. */
export const RUNTIME_AUXILIARY_NON_PTG_ROUTE_FAMILIES = [
  'item/raw-material',
  'workspace/tasks',
  'workspace/operations',
  'flow/executions',
  'appeal/campaign',
  'approvals/pending',
  'approvals/history',
  'system/audit',
  'system/site-join-applications',
  'system/site-readiness',
  'system/schema',
] as const;

/** Domain-only utility runtime routes (workspace state has domain, no subdomain). */
export const RUNTIME_AUXILIARY_DOMAIN_ONLY_SCOPES = ['automation', 'export'] as const;

const NON_PTG_ROUTE_FAMILY_SET = new Set<string>(RUNTIME_AUXILIARY_NON_PTG_ROUTE_FAMILIES);
const DOMAIN_ONLY_SCOPE_SET = new Set<string>(RUNTIME_AUXILIARY_DOMAIN_ONLY_SCOPES);

function normalizeRouteSegment(value: string | undefined): string | undefined {
  const trimmed = value?.trim().toLowerCase();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/** Whether auxiliary Timeline/Audit APIs should load for this workbench route. */
export function isRuntimeWorkbenchAuxiliaryScope(domain?: string, subdomain?: string): boolean {
  if (isPtgReferenceWorkbenchScope(domain, subdomain)) {
    return true;
  }
  const normalizedDomain = normalizeRouteSegment(domain);
  const normalizedSubdomain = normalizeRouteSegment(subdomain);
  if (!normalizedDomain) {
    return false;
  }
  if (!normalizedSubdomain) {
    return DOMAIN_ONLY_SCOPE_SET.has(normalizedDomain);
  }
  return NON_PTG_ROUTE_FAMILY_SET.has(`${normalizedDomain}/${normalizedSubdomain}`);
}

/**
 * Server-backed timeline/audit fetch scope — runtime routes OR domain cockpit pilots
 * with a scoped object selection on BusinessWorkbenchShell.
 */
export function isWorkbenchAuxiliaryServerDataScope(input: {
  domain?: string;
  subdomain?: string;
  businessDomain?: string;
  hasScopedObject?: boolean;
}): boolean {
  if (isRuntimeWorkbenchAuxiliaryScope(input.domain, input.subdomain)) {
    return true;
  }
  const businessDomain = input.businessDomain?.trim();
  if (!businessDomain || !isDomainCockpitPilotDomain(businessDomain)) {
    return false;
  }
  return input.hasScopedObject !== false;
}
